import { useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

// ── Preset quick-start palettes ──────────────────────────────────────────────
const PALETTE_OPTIONS = [
  { id: "atlas",     name: "HubSpot default", colors: ["#50e3c2", "#3facc6", "#4a5b68", "#7d8b96", "#cccccc", "#a7f0df", "#323c44", "#4a5b68", "#9fdbe8", "#57c1a9"] },
  { id: "boardroom", name: "Revenue ops",     colors: ["#50e3c2", "#323c44", "#3facc6", "#4a5b68", "#7d8b96", "#a7f0df", "#4a5b68", "#7d8b96", "#9fdbe8", "#57c1a9"] },
  { id: "sunrise",   name: "Pipeline focus",  colors: ["#50e3c2", "#57c1a9", "#3facc6", "#9fdbe8", "#323c44", "#cccccc", "#4a5b68", "#4a5b68", "#7d8b96", "#a7f0df"] },
];

export { PALETTE_OPTIONS };

// ── Default 10-colour set (atlas) ────────────────────────────────────────────
export const DEFAULT_CUSTOM_COLORS = PALETTE_OPTIONS[0].colors;

// ── Single colour swatch + native picker ─────────────────────────────────────
function ColorSlot({ index, color, onChange }) {
  const inputRef = useRef(null);
  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        title={`Colour ${index + 1} — click to change`}
        onClick={() => inputRef.current?.click()}
        className="h-9 w-9 rounded-lg border-2 border-white shadow ring-1 ring-black/10 transition hover:scale-110 hover:ring-[#57c1a9] focus:outline-none focus:ring-2 focus:ring-[#57c1a9]"
        style={{ backgroundColor: color }}
      />
      {/* visually hidden native colour picker */}
      <input
        ref={inputRef}
        type="color"
        value={color}
        onChange={(e) => onChange(index, e.target.value)}
        className="sr-only"
        aria-label={`Colour ${index + 1}`}
      />
      <span className="text-[10px] text-[#7d8b96]">{index + 1}</span>
    </div>
  );
}

// ── Main settings panel ───────────────────────────────────────────────────────
export default function SettingsPanel({ open, onOpenChange, settings, onUpdate }) {
  const customColors = settings.customColors && settings.customColors.length === 10
    ? settings.customColors
    : DEFAULT_CUSTOM_COLORS;

  const handleColorChange = (index, value) => {
    const next = [...customColors];
    next[index] = value;
    onUpdate("customColors", next);
  };

  const applyPreset = (colors) => {
    onUpdate("customColors", [...colors]);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto border-l border-[#cccccc] bg-white sm:max-w-xl" data-testid="report-settings-panel">
        <SheetHeader>
          <SheetTitle className="font-sans text-2xl text-[#323c44]" data-testid="report-settings-title">Report styling</SheetTitle>
          <SheetDescription className="text-sm text-[#4a5b68]" data-testid="report-settings-description">
            Tune chart colors, rounding, labels, and light branding without adding clutter to the main report surface.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-8 space-y-6">
          {/* Section names + subtitles */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-[#323c44]">Section names</p>
            {[
              ["title",                   "subtitle",                   "MRR title",                "MRR subtitle"],
              ["arrTitle",                "arrSubtitle",                "ARR title",                "ARR subtitle"],
              ["revenueByClientTitle",    "revenueByClientSubtitle",    "Revenue by Client title",  "Revenue by Client subtitle"],
            ].map(([titleKey, subtitleKey, titleLabel, subtitleLabel]) => (
              <div key={titleKey} className="grid gap-2 sm:grid-cols-2">
                <label className="space-y-1">
                  <span className="text-xs text-[#4a5b68]">{titleLabel}</span>
                  <input
                    className="h-9 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                    value={settings[titleKey] || ""}
                    onChange={(e) => onUpdate(titleKey, e.target.value)}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-[#4a5b68]">{subtitleLabel}</span>
                  <input
                    className="h-9 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-3 text-sm text-[#323c44]"
                    value={settings[subtitleKey] || ""}
                    onChange={(e) => onUpdate(subtitleKey, e.target.value)}
                  />
                </label>
              </div>
            ))}
          </div>

          {/* Currency + decimals */}
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm font-medium text-[#323c44]" data-testid="settings-currency-group">
              <span>Currency</span>
              <select
                className="h-10 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-4 text-[#323c44]"
                value={settings.currency}
                onChange={(e) => onUpdate("currency", e.target.value)}
                data-testid="settings-currency-select"
              >
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
              </select>
            </label>
            <label className="space-y-2 text-sm font-medium text-[#323c44]" data-testid="settings-decimals-group">
              <span>Decimal places</span>
              <input
                type="number"
                min="0"
                max="4"
                className="h-10 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-4 text-[#323c44]"
                value={settings.decimalPlaces}
                onChange={(e) => onUpdate("decimalPlaces", Number(e.target.value))}
                data-testid="settings-decimals-input"
              />
            </label>
          </div>

          {/* Toggles */}
          <div className="grid gap-3" data-testid="settings-toggle-group">
            {[
              ["roundWholeNumbers", "Round to whole numbers"],
              ["showTotals",        "Show totals above bars"],
              ["showLegend",        "Show chart legend"],
            ].map(([key, label]) => (
              <label
                key={key}
                className="flex items-center justify-between rounded-sm border border-[#cccccc] px-4 py-3 text-sm text-[#323c44]"
                data-testid={`settings-toggle-${key}`}
              >
                <span>{label}</span>
                <input
                  type="checkbox"
                  checked={settings[key]}
                  onChange={(e) => onUpdate(key, e.target.checked)}
                  data-testid={`settings-toggle-${key}-checkbox`}
                />
              </label>
            ))}
          </div>

          {/* Date format */}
          <div className="space-y-3" data-testid="settings-date-format-group">
            <label className="text-sm font-medium text-[#323c44]" htmlFor="date-format-select">Date format</label>
            <select
              id="date-format-select"
              className="h-10 w-full rounded-sm border border-[#cccccc] bg-[#f5f5f5] px-4 text-[#323c44]"
              value={settings.dateFormat}
              onChange={(e) => onUpdate("dateFormat", e.target.value)}
              data-testid="settings-date-format-select"
            >
              <option value="short">MMM YYYY</option>
              <option value="compact">MMM YY</option>
              <option value="long">MMMM YYYY</option>
            </select>
          </div>

          {/* Chart colours ── custom picker */}
          <div className="space-y-4" data-testid="settings-palette-group">
            <p className="text-sm font-medium text-[#323c44]">Chart colours</p>

            {/* 10 individual pickers */}
            <div className="grid grid-cols-10 gap-2">
              {customColors.map((color, i) => (
                <ColorSlot key={i} index={i} color={color} onChange={handleColorChange} />
              ))}
            </div>

            {/* Quick-start presets */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#7d8b96]">Quick start</p>
              <div className="flex flex-col gap-2">
                {PALETTE_OPTIONS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset.colors)}
                    className="flex items-center justify-between rounded-sm border border-[#cccccc] bg-white px-4 py-3 text-left transition hover:border-[#57c1a9] hover:bg-[#e8faf5]"
                    data-testid={`palette-option-${preset.id}`}
                  >
                    <span className="text-sm font-semibold text-[#323c44]">{preset.name}</span>
                    <span className="flex gap-1.5">
                      {preset.colors.map((c, i) => (
                        <span
                          key={i}
                          className="h-4 w-4 rounded-full border border-black/10"
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
